/** The app's entry point, loaded by `src/index.html`. */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { prewarmRoutes } from "./routePrewarm";
import { unregisterAll } from "./serviceWorkerReset";

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <App />
  </StrictMode>
);

// Reuse the root across hot reloads instead of creating a second one for the
// same element. The result has to be captured in a local: in production Bun
// inlines `import.meta.hot.data` to `{}`, so this whole expression minifies to
// `createRoot(elem)` — but reading `import.meta.hot.data.root` back on a second
// line would read off a fresh `{}` and blow up on `undefined.render`.
// https://bun.com/docs/bundler/hot-reloading#import-meta-hot-data
// biome-ignore lint/suspicious/noAssignInExpressions: Bun only minifies the production case away when the assignment is written inline.
const root = (import.meta.hot.data.root ??= createRoot(elem));
root.render(app);

// Decode the other pages' artwork once this one is loaded and the main thread is
// idle, so clicking Watch / Live / Contact paints in the frame it is clicked in.
// Called here rather than from Home, so landing straight on a subpage warms the
// way back too. See src/routePrewarm.ts.
prewarmRoutes();

// Production only: `dist/sw.js` exists only in a real build, and in dev a worker
// would serve stale bundles at `bun --hot`. See src/sw/worker.ts for why the
// site needs one at all.
//
// The URL is resolved against this module rather than written as `/sw.js`: the
// site deploys to a GitHub *project* page and lives under `/elsa/`, where an
// absolute path would 404 and also fall outside the scope a worker is allowed
// to claim. Letting the browser infer scope from the script's own location is
// the only spelling that works on both a subpath and a root deploy.
if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register(new URL("sw.js", import.meta.url)).catch(() => {
      // A site that renders is more important than a site that caches.
    });
  });
} else if (process.env.NODE_ENV !== "production") {
  // Dev only: evict any worker claiming this origin, ours or a foreign one left
  // over from another project on the same port. See src/serviceWorkerReset.ts.
  void unregisterAll(navigator.serviceWorker);
}
